import { canTransactionBeDeleted } from "@common/helpers/transactions";
import { CATEGORY_RELATED_ACTIONS } from "@common/requiredActions";
import {
    Transaction,
    TransactionJournalEntry,
    TransactionStatus,
} from "@common/types/transaction";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { difference } from "@common/helpers/collections";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { Menu } from "@design-system/Menu/Menu.lib";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { useOverlays } from "contexts/OverlayContext";
import { useCopyToClipboard } from "@hooks/useCopyToClipboard";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    IgnoreRequirementsIcon,
    LinkIcon,
    RequirementsIcon,
    SplitSIcon,
} from "@icons";
import { useLedgers } from "@api/ledger.api";
import { GlDrilldownDrawer } from "@components/accounting/generalLedger/GlDrilldownDrawer";
import { AccountingReportFilters } from "@components/accounting/types";
import { HStack } from "@design-system/HStack/HStack";
import { detailsAsideCommandBarClassName } from "@design-system/DetailsAside/DetailsAside.styles";
import { DeleteTransactionModal } from "../DeleteTransactionModal";
import { createTransactionsUrl } from "../lib/transactionsUrl";
import { PaginatedTransactionsActionsContext } from "../PaginatedTransactionsTable.context";
import { TransactionDetailsContext } from "./transactionDetails.context";
import { TransactionGoogleSearchButton } from "./TransactionGoogleSearchButton";
import {
    getManualTransactionUpdateAvailability,
    ManualTransactionUpdateSource,
} from "./manualTransactionUpdateAvailability";
import { UpdateManualTransactionModal } from "./UpdateManualTransactionModal";
import { useOpenScheduleJournalEntries } from "@components/accounting/schedules/useOpenScheduleJournalEntries";
import { useTypedFlags } from "@hooks/useTypedFlags";

function pickJournalEntryForDrawer(
    transaction: Transaction,
): TransactionJournalEntry | undefined {
    const journalEntries = transaction.journalEntries ?? [];
    if (journalEntries.length === 0) {
        return undefined;
    }
    const forEntity = journalEntries.filter(
        (journalEntry) =>
            journalEntry.accountingLedger == null ||
            journalEntry.accountingLedger.entityId === transaction.entityId,
    );
    const pool = forEntity.length > 0 ? forEntity : journalEntries;
    return (
        pool.find(
            (journalEntry) =>
                journalEntry.accountingLedger?.basis ===
                AccountingLedgerBasis.cash,
        ) ?? pool[0]
    );
}

interface Props {
    transaction: ManualTransactionUpdateSource;
    onClose(): void;
    isLocked?: boolean;
}

export const TransactionDetailsFooter: React.FC<Props> = ({
    transaction,
    onClose,
    isLocked,
}) => {
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const manualUpdate = getManualTransactionUpdateAvailability({
        transaction,
        isLocked: Boolean(isLocked),
    });
    const { openOverlay } = useOverlays();
    const { setShownTransactionId } = useContext(
        PaginatedTransactionsActionsContext,
    );

    const handleOpenMatch = useCallback(
        () =>
            openOverlay("transactionMatch", { transactionId: transaction.id }),
        [openOverlay, transaction],
    );
    const { onUpdate, origins } = useContext(TransactionDetailsContext);
    const canSplitTransaction =
        transaction.status === TransactionStatus.COMPLETED;
    const canDeleteTransaction = canTransactionBeDeleted(transaction, origins);

    const splitTransactionMessage = canSplitTransaction
        ? "Split transaction"
        : `Cannot split ${transaction.status} transactions`;
    const requirementsSnoozeMessage = transaction.requirementsSnoozed
        ? "Enable Requirements"
        : "Snooze Requirements";

    const showRequirementsSnoozing = useMemo(
        () =>
            transaction.requirementsSnoozed ||
            difference(transaction.requiredActions, CATEGORY_RELATED_ACTIONS)
                .length > 0,
        [transaction.requirementsSnoozed, transaction.requiredActions],
    );

    const [showGlDrawer, setShowGlDrawer] = useState(false);

    const { data: ledgers = [] } = useLedgers(transaction.entityId);
    const fallbackLedgerId = ledgers[0]?.id;

    const journalEntry = pickJournalEntryForDrawer(transaction);
    const canViewJournalEntry = transaction.isBusiness;
    const { schedules: isSchedulesEnabled } = useTypedFlags();
    const onViewRecognitionSchedule = useOpenScheduleJournalEntries(
        transaction.schedule
            ? {
                  templateId: transaction.schedule.scheduleTemplateId,
                  scheduleApplicationId:
                      transaction.schedule.scheduleApplicationId,
              }
            : null,
    );

    const familyTransactionIds = useMemo(() => {
        const splitIds = (transaction.splits ?? []).map((split) => split.id);
        return [...new Set([transaction.id, ...splitIds])];
    }, [transaction.id, transaction.splits]);

    const glDrawerFilters = useMemo<AccountingReportFilters>(
        () => ({
            transactionIds: familyTransactionIds,
            isAllTime: true,
            ledgerId: journalEntry?.accountingLedger?.id ?? fallbackLedgerId,
        }),
        [
            familyTransactionIds,
            journalEntry?.accountingLedger?.id,
            fallbackLedgerId,
        ],
    );

    const glDrawerEntityIds = useMemo(
        () => [transaction.entityId],
        [transaction.entityId],
    );

    const handleViewJournalEntry = useCallback(() => {
        if (!journalEntry?.referenceNumber) {
            setShowGlDrawer(true);
            return;
        }
        openOverlay("journalEntryDrawer", {
            entity: transaction.entity,
            journalEntryId: journalEntry.referenceNumber,
            ledgerId: journalEntry.accountingLedger?.id,
            ledgerBasis: journalEntry.accountingLedger?.basis,
        });
    }, [journalEntry, transaction.entity, openOverlay]);

    const copyToClipboard = useCopyToClipboard({
        successMessage: "Transaction URL copied to clipboard",
    });

    const handleCopyTransactionLink = useCallback(() => {
        const transactionUrl = `${window.location.origin}${createTransactionsUrl(
            {
                transactionIds: [transaction.id],
            },
        )}`;

        copyToClipboard(transactionUrl);
    }, [transaction.id, copyToClipboard]);

    const handleSplitSave = useCallback(
        (savedTransactions: Transaction[]) => {
            if (!savedTransactions.some((t) => t.id === transaction.id)) {
                setShownTransactionId(undefined);
            }
        },
        [transaction.id, setShownTransactionId],
    );

    return (
        <>
            <CommandIsland className={detailsAsideCommandBarClassName}>
                <HStack gap={8}>
                    <Tooltip content={splitTransactionMessage}>
                        <Button
                            variant="secondary"
                            iconOnly
                            aria-label={splitTransactionMessage}
                            onClick={() =>
                                openOverlay<"transactionSplit">(
                                    "transactionSplit",
                                    {
                                        transaction,
                                        onSplitSave: handleSplitSave,
                                    },
                                )
                            }
                            disabled={!canSplitTransaction || isLocked}
                            data-testid="transaction-details-split"
                            size="sm"
                        >
                            <SplitSIcon />
                        </Button>
                    </Tooltip>
                    {showRequirementsSnoozing && (
                        <Tooltip
                            key={
                                transaction.requirementsSnoozed
                                    ? "snoozed"
                                    : "enabled"
                            }
                            content={requirementsSnoozeMessage}
                        >
                            <Button
                                variant="secondary"
                                iconOnly
                                size="sm"
                                aria-label={requirementsSnoozeMessage}
                                onClick={() =>
                                    onUpdate({
                                        requirementsSnoozed:
                                            !transaction.requirementsSnoozed,
                                    })
                                }
                                data-testid="transaction-details-snooze-requirements"
                            >
                                {transaction.requirementsSnoozed ? (
                                    <RequirementsIcon />
                                ) : (
                                    <IgnoreRequirementsIcon />
                                )}
                            </Button>
                        </Tooltip>
                    )}
                    <Tooltip content="Match transaction">
                        <Button
                            variant="secondary"
                            iconOnly
                            onClick={handleOpenMatch}
                            disabled={isLocked}
                            aria-label="Match transaction"
                            data-testid="transaction-details-match"
                            size="sm"
                        >
                            <LinkIcon />
                        </Button>
                    </Tooltip>
                    <TransactionGoogleSearchButton transaction={transaction} />
                </HStack>
                <Menu.More>
                    <Menu.Item
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCopyTransactionLink();
                        }}
                    >
                        Copy transaction link
                    </Menu.Item>
                    {manualUpdate.isManualTransaction && (
                        <Menu.Item
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowUpdateModal(true);
                            }}
                            disabled={manualUpdate.disabledReason !== null}
                            disabledReason={manualUpdate.disabledReason}
                        >
                            Update transaction
                        </Menu.Item>
                    )}
                    {canViewJournalEntry && (
                        <Menu.Item
                            onClick={(e) => {
                                e.stopPropagation();
                                handleViewJournalEntry();
                            }}
                        >
                            View journal entry
                        </Menu.Item>
                    )}
                    {isSchedulesEnabled && onViewRecognitionSchedule && (
                        <Menu.Item
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewRecognitionSchedule();
                            }}
                        >
                            View recognition schedule
                        </Menu.Item>
                    )}
                    {canDeleteTransaction && (
                        <Menu.Item
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowDeleteModal(true);
                            }}
                            destructive
                        >
                            Delete
                        </Menu.Item>
                    )}
                </Menu.More>
            </CommandIsland>
            <DeleteTransactionModal
                show={showDeleteModal}
                onBack={() => setShowDeleteModal(false)}
                onDeleteSuccess={() => {
                    setShowDeleteModal(false);
                    onClose();
                }}
                onHide={() => setShowDeleteModal(false)}
                transaction={transaction}
            />
            {showUpdateModal && (
                <UpdateManualTransactionModal
                    show
                    transaction={transaction}
                    onClose={() => setShowUpdateModal(false)}
                />
            )}
            <GlDrilldownDrawer
                open={showGlDrawer}
                onClose={() => setShowGlDrawer(false)}
                filters={glDrawerFilters}
                entityIds={glDrawerEntityIds}
            />
        </>
    );
};
